import { useState, useEffect, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import AuthLegalFooter from '../components/AuthLegalFooter';
import SystemStatusBanner from '../components/SystemStatusBanner';
import '../styles/login.css';
import '../styles/system-status.css';

interface Session {
  pseudo: string;
  pseudoDisplay: string;
  compet: boolean;
  hasCompetitionAccess?: boolean;
  token?: string;
  hashPass?: string;
}

interface LoginProps {
  onLogin: (session: Session) => void;
}

interface DevStatus {
  available: boolean;
  godMode: boolean;
  active: boolean;
  interval: number;
}

interface LoginResponse {
  success: boolean;
  token: string;
  hashPass: string;
  message?: string;
  error?: string;
  user: {
    pseudo: string;
    pseudoDisplay: string;
    compet: boolean;
    licenceValid: boolean;
    emailVerified: boolean;
    registered: string;
  };
  god_mode?: boolean;
  dev_mode?: boolean;
  auth_result?: {
    user_exists: boolean;
    password_valid: boolean;
    original_error?: string;
  };
}

interface OAuthProvider {
  id: string;
  name: string;
  authUrl: string;
}

import { BACKEND_URL } from '../config';

const DEFAULT_OAUTH_PROVIDERS: OAuthProvider[] = [
  { id: 'google', name: 'Google', authUrl: `${BACKEND_URL}/api/auth/oauth/start/google` },
  { id: 'facebook', name: 'Facebook', authUrl: `${BACKEND_URL}/api/auth/oauth/start/facebook` },
];

export default function Login({ onLogin }: LoginProps) {
  const [pseudo, setPseudo] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPseudoForm, setShowPseudoForm] = useState(false);
  const [devStatus, setDevStatus] = useState<DevStatus | null>(null);
  const [godModeInfo, setGodModeInfo] = useState<{
    used: boolean;
    userExists: boolean;
    passwordValid: boolean;
  } | null>(null);
  const [oauthProviders, setOauthProviders] = useState<OAuthProvider[]>(DEFAULT_OAUTH_PROVIDERS);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/dev/simulation`)
      .then(res => res.json())
      .then(data => { if (data.available) setDevStatus(data); })
      .catch(() => {});

    fetch(`${BACKEND_URL}/api/auth/oauth/providers`)
      .then(res => res.json())
      .then(data => { if (data.providers?.length) setOauthProviders(data.providers); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setGodModeInfo(null);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo: pseudo.trim(), password, compet: false }),
      });

      const data = await response.json() as LoginResponse;

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Erreur de connexion');
      }

      if (data.god_mode || data.dev_mode) {
        const authResult = data.auth_result;
        setGodModeInfo({
          used: true,
          userExists: authResult?.user_exists ?? (data.user.registered !== 'dev'),
          passwordValid: authResult?.password_valid ?? false,
        });
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const hasLicence = data.user.licenceValid === true;

      onLogin({
        pseudo: data.user.pseudo,
        pseudoDisplay: data.user.pseudoDisplay,
        compet: hasLicence,
        hasCompetitionAccess: hasLicence,
        token: data.token,
        hashPass: data.hashPass,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <SystemStatusBanner />
      <div className="login-bg" />

      <div className="login-card">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">Le tarot en ligne, entre amis ou en compétition</p>
        </div>

        <div className="login-body">
          {/* OAuth buttons */}
          <div className="oauth-buttons">
            {oauthProviders.map(provider => (
              <a
                key={provider.id}
                href={provider.authUrl}
                className={`oauth-btn oauth-btn-${provider.id}`}
              >
                <OAuthIcon provider={provider.id} />
                <span>Continuer avec {provider.name}</span>
              </a>
            ))}
          </div>

          {!showPseudoForm ? (
            <button
              type="button"
              className="oauth-btn oauth-btn-pseudo"
              onClick={() => setShowPseudoForm(true)}
            >
              <PseudoIcon />
              <span>Continuer avec un pseudo</span>
            </button>
          ) : (
            <div className="pseudo-form-section">
              <div className="login-divider"><span>Connexion avec pseudo</span></div>

              <form onSubmit={handleSubmit} className="login-form">
                {error && <div className="login-error">{error}</div>}

                {godModeInfo && (
                  <div className={`god-mode-result ${godModeInfo.userExists && godModeInfo.passwordValid ? 'valid' : 'bypassed'}`}>
                    <div className="god-mode-header">
                      🔱 Connexion autorisée (Mode Dieu)
                    </div>
                    <div className="god-mode-details">
                      <div className={`detail-item ${godModeInfo.userExists ? 'ok' : 'ko'}`}>
                        {godModeInfo.userExists ? '✓' : '✗'} Utilisateur {godModeInfo.userExists ? 'existe' : 'n\'existe pas'}
                      </div>
                      <div className={`detail-item ${godModeInfo.passwordValid ? 'ok' : 'ko'}`}>
                        {godModeInfo.passwordValid ? '✓' : '✗'} Mot de passe {godModeInfo.passwordValid ? 'correct' : 'incorrect'}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <input
                    type="text"
                    id="pseudo"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    placeholder="Pseudo"
                    autoComplete="username"
                    autoFocus
                    required
                  />
                </div>

                <div className="form-group">
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mot de passe"
                    autoComplete="current-password"
                    required
                  />
                </div>

                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>

                <div className="login-form-links">
                  <Link to="/forgot-password" className="login-form-back">
                    Mot de passe oublié ?
                  </Link>
                  <span className="login-form-sep" aria-hidden="true">•</span>
                  <Link to="/auth/magic-link" className="login-form-back">
                    Lien de connexion par email
                  </Link>
                </div>
              </form>
            </div>
          )}

          <div className="login-links">
            <span>Pas encore de compte ?</span>
            <Link to="/register">Créer un compte</Link>
          </div>

          <AuthLegalFooter />
        </div>

        {devStatus?.available && (
          <div className="login-dev-footer">
            {devStatus.godMode && <span>🔱 Mode Dieu</span>}
            {devStatus.active && <span>🎮 Simulation</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function PseudoIcon() {
  return (
    <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function OAuthIcon({ provider }: { provider: string }) {
  switch (provider) {
    case 'google':
      return (
        <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
        </svg>
      );
    default:
      return null;
  }
}
